import { useCallback } from 'react';

import { Element, NodeEntry, Text } from 'slate';

import { LOGICAL_OPERATOR_SUGGESTIONS, GROUPING_OPERATOR_SUGGESTIONS } from './constants';
import { CustomEditor } from './types';
import { parseExpression } from './utils';

/**
 * Plugin to handle node spacing and layout
 */
// [PURPOSE]: Defines a Slate plugin that overrides the editor's 'isInline' and 'isVoid' methods to specify custom behavior for the query builder element types (chip, simple-chip, logical-operator, grouping-operator).
export const withNodeSpacing = (editor: CustomEditor) => {
  const { isInline, isVoid } = editor;
  const customTokens = new Set(['chip', 'simple-chip', 'logical-operator', 'grouping-operator']);

  editor.isInline = (element: Element) => {
    return customTokens.has(element.type) || isInline(element);
  };

  editor.isVoid = (element: Element) => {
    return customTokens.has(element.type) || isVoid(element);
  };

  return editor;
};

/**
 * Hook for decorating nodes with styles
 */
// [PURPOSE]: Defines a React hook that returns Slate's 'decorate' function. This function applies dynamic styling (like color or font weight) to different parts of text nodes based on regex matching or parsing (e.g., styling fields, operators, values, parentheses, and logical operators differently).
export const useDecorate = () => {
  // [PURPOSE]: The callback function returned by the hook, which Slate calls for each node entry to determine decorations (dynamic styles).
  return useCallback(([node, path]: NodeEntry) => {
    const ranges: any[] = [];

    // Only process text nodes with content
    if (!Text.isText(node) || !node.text || typeof node.text !== 'string') {
      return ranges;
    }

    const { text } = node;

    // Then parse text into field/operator/value parts
    const parsed = parseExpression(text);
    const [
      [prefixOperator, field, operator, value],
      { identifierLocation, notLocation, operatorLocation, valueLocation },
    ] = parsed;

    // Style different parts of the text using indexOf for more precise positioning
    if (field && identifierLocation) {
      ranges.push({
        anchor: { offset: identifierLocation.start?.offset, path },
        field: true,
        focus: { offset: identifierLocation.end?.offset, path },
      });
    }

    if (operator && operatorLocation) {
      ranges.push({
        anchor: { offset: operatorLocation.start?.offset, path },
        focus: { offset: operatorLocation.end?.offset, path },
        operator: true,
      });
    }

    if (prefixOperator && notLocation) {
      ranges.push({
        anchor: { offset: notLocation.start?.offset, path },
        focus: { offset: notLocation.end?.offset, path },
        logicalOperator: true,
      });
    }

    if (value && valueLocation) {
      const anchorOffset =
        typeof valueLocation.start === 'number' ? valueLocation.start : valueLocation.start?.offset;
      const focusOffset =
        typeof valueLocation.end === 'number' ? valueLocation.end : valueLocation.end?.offset;

      ranges.push({
        anchor: { offset: anchorOffset, path },
        focus: { offset: focusOffset, path },
        value: true,
      });
    }

    // Style logical operators (with word boundary and case insensitive check)
    for (const op of LOGICAL_OPERATOR_SUGGESTIONS) {
      // Use case-insensitive regex with word boundary
      const regex = new RegExp(`\\b${op}\\b`, 'gi');
      let match;
      while ((match = regex.exec(text)) !== null) {
        // Check if match has trailing space
        const hasTrailingSpace =
          match.index + match[0].length < text.length &&
          /\s/.test(text[match.index + match[0].length]);

        // Only style if it has trailing space
        if (hasTrailingSpace) {
          ranges.push({
            anchor: { offset: match.index, path },
            focus: { offset: match.index + match[0].length, path },
            logicalOperator: true,
          });
        }
      }
    }

    // Style grouping operators (except parentheses, which were handled earlier)
    for (const op of GROUPING_OPERATOR_SUGGESTIONS) {
      if (op !== '(' && op !== ')') {
        // Skip parentheses as they were handled earlier
        let index = 0;
        while ((index = text.indexOf(op, index)) !== -1) {
          ranges.push({
            anchor: { offset: index, path },
            focus: { offset: index + op.length, path },
            groupingOperator: true,
          });
          index += op.length;
        }
      }
    }

    return ranges;
  }, []);
};
